import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Today as TodayIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Code as CodeIcon,
  Email as EmailIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getTextColorForBackground } from '../utils/colorUtils';
import { formatDuration } from '../utils/timeUtils';
import PointageForm from '../components/pointage/PointageForm';
import HoursInput from '../components/HoursInput';
import PointageFilters from '../components/pointage/PointageFilters';

const PointageMensuelMobile = ({
  selectedDate,
  selectedUser,
  users,
  onUserChange,
  onDateChange,
  disabled,
  pointages,
  onQuickAdd,
  onPointageClick,
  showWeekends,
  dayContent,
  filteredDays,
  dayPointages,
  formData,
  onFormChange,
  releveCodes,
  activities,
  renderEmail,
  selectedReleveCodes,
  onCodeReleveChange,
  onEditClick,
  onDuplicateClick,
  onDeleteClick,
  onFavoriteClick,
  anchorEl,
  selectedPointage,
  onMenuClose,
  isEditDialogOpen,
  onCloseEditDialog,
  editedPointage,
  onEditedPointageChange,
  onSaveEditedPointage,
  ...props
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [activeView, setActiveView] = React.useState('calendar');

  const renderDayCard = (day, dayPointages) => (
    <Paper
      key={day.toISOString()}
      elevation={2}
      sx={{
        p: 1,
        mb: 1,
        backgroundColor: 'background.paper',
        position: 'relative'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {format(day, 'EEEE d MMMM', { locale: fr })}
        </Typography>
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            onQuickAdd(day);
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      <Stack spacing={1}>
        {(dayPointages || []).map((pointage) => (
          <Box
            key={pointage.id}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              onPointageClick(pointage, {
                clientX: rect.left,
                clientY: rect.top + rect.height
              });
            }}
            sx={{
              p: 1,
              borderRadius: 1,
              backgroundColor: pointage.backgroundColor,
              color: getTextColorForBackground(pointage.backgroundColor),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative'
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {pointage.releve_codes?.code} - {formatDuration(pointage.hours)}
              </Typography>
              <Typography variant="caption" display="block">
                {pointage.activities?.name}
                {pointage.description && `: ${pointage.description}`}
              </Typography>
            </Box>
            {pointage.is_favorite && (
              <Badge color="warning">
                <FavoriteIcon fontSize="small" />
              </Badge>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );

  const renderMobileMenu = () => (
    <Drawer
      anchor="bottom"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh'
        }
      }}
    >
      <List>
        <ListItem button onClick={() => setActiveView('calendar')}>
          <ListItemIcon><TodayIcon /></ListItemIcon>
          <ListItemText primary="Calendrier" />
        </ListItem>
        <ListItem button onClick={() => setActiveView('favorites')}>
          <ListItemIcon><FavoriteIcon /></ListItemIcon>
          <ListItemText primary="Favoris" />
        </ListItem>
        <ListItem button onClick={() => setActiveView('codes')}>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Codes relève" />
        </ListItem>
        <ListItem button onClick={() => setActiveView('email')}>
          <ListItemIcon><EmailIcon /></ListItemIcon>
          <ListItemText primary="Rapport email" />
        </ListItem>
        <Divider />
        <ListItem>
          <Stack direction="row" spacing={1}>
            <Chip 
              icon={<CalendarViewWeekIcon />} 
              label="Week-ends"
              onClick={props.onToggleWeekends}
              color={showWeekends ? 'primary' : 'default'}
            />
          </Stack>
        </ListItem>
      </List>
    </Drawer>
  );

  const renderFormContent = () => (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Nouveau pointage
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PointageForm
          formData={formData}
          onFormChange={onFormChange}
          releveCodes={releveCodes}
          activities={activities}
        />
      </Box>
    </Paper>
  );

  const renderCodesContent = () => (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Codes de relève disponibles
      </Typography>
      <Grid container spacing={1}>
        {releveCodes.map(code => (
          <Grid item xs={12} key={code.id}>
            <Paper
              elevation={1}
              sx={{
                p: 1,
                backgroundColor: code.color,
                color: getTextColorForBackground(code.color)
              }}
            >
              <Typography variant="subtitle2">
                {code.code} {code.role ? `(${code.role})` : ''}
              </Typography>
              {code.hours_per_month && (
                <Typography variant="caption" display="block">
                  Dotation: {formatDuration(code.hours_per_month)}/
                  {code.particularity === 'annual' ? 'an' : 'mois'}
                </Typography>
              )}
              {code.monthly_limit && (
                <Typography variant="caption" display="block">
                  Maximum: {formatDuration(code.monthly_limit)}
                </Typography>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {code.mandate_description || 'Pas de description'}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const renderEmailContent = () => (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Rapport mensuel
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Codes Relève à inclure</InputLabel>
        <Select
          multiple
          value={selectedReleveCodes}
          label="Codes Relève à inclure"
          onChange={onCodeReleveChange}
        >
          {props.codeReleveOptions?.map((code) => (
            <MenuItem key={code} value={code}>
              {code}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {renderEmail()}
    </Paper>
  );

  const renderContextMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onMenuClose}
      MenuListProps={{
        'aria-labelledby': 'pointage-menu'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '& .MuiPaper-root': {
          width: '100%',
          maxWidth: '280px',
        }
      }}
    >
      <MenuItem onClick={onEditClick}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Modifier</ListItemText>
      </MenuItem>

      <MenuItem onClick={onDuplicateClick}>
        <ListItemIcon>
          <DuplicateIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Dupliquer</ListItemText>
      </MenuItem>

      <MenuItem onClick={onFavoriteClick}>
        <ListItemIcon>
          {selectedPointage?.is_favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </ListItemIcon>
        <ListItemText>
          {selectedPointage?.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        </ListItemText>
      </MenuItem>

      <MenuItem onClick={onDeleteClick}>
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Supprimer</ListItemText>
      </MenuItem>
    </Menu>
  );

  const renderEditDialog = () => (
    <Dialog 
      open={isEditDialogOpen} 
      onClose={onCloseEditDialog}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default'
        }
      }}
    >
      <DialogTitle>Modifier le pointage</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <DatePicker
            label="Date"
            value={editedPointage?.date}
            onChange={(newDate) => onEditedPointageChange(prev => ({
              ...prev,
              date: newDate
            }))}
            renderInput={(params) => <TextField {...params} />}
          />
          <FormControl fullWidth>
            <InputLabel>Code Relève</InputLabel>
            <Select
              value={editedPointage?.releveCodeId || ''}
              label="Code Relève"
              onChange={(e) => onEditedPointageChange(prev => ({
                ...prev,
                releveCodeId: e.target.value
              }))}
            >
              {releveCodes.map((code) => (
                <MenuItem key={code.id} value={code.id}>
                  {code.code} {code.role ? `(${code.role})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Activité</InputLabel>
            <Select
              value={editedPointage?.activityId || ''}
              label="Activité"
              onChange={(e) => onEditedPointageChange(prev => ({
                ...prev,
                activityId: e.target.value
              }))}
            >
              {activities.map((activity) => (
                <MenuItem key={activity.id} value={activity.id}>
                  {activity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={editedPointage?.description || ''}
            onChange={(e) => onEditedPointageChange(prev => ({
              ...prev,
              description: e.target.value
            }))}
            multiline
            rows={4}
            fullWidth
          />
          <HoursInput
            value={editedPointage?.hours || 0}
            onChange={(newValue) => onEditedPointageChange(prev => ({
              ...prev,
              hours: newValue
            }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editedPointage?.on_report || false}
                onChange={(e) => onEditedPointageChange(prev => ({
                  ...prev,
                  on_report: e.target.checked
                }))}
              />
            }
            label="Fait Marquant"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseEditDialog}>Annuler</Button>
        <Button onClick={onSaveEditedPointage} color="primary">
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderFilters = () => (
    <Box sx={{ mb: 2 }}>
      <PointageFilters
        users={users}
        selectedUser={selectedUser}
        selectedDate={selectedDate}
        onUserChange={onUserChange}
        onDateChange={onDateChange}
        disabled={disabled}
      />
    </Box>
  );

  return (
    <Box sx={{ pb: 7 }}>
      {/* Filtres en haut */}
      {renderFilters()}

      {/* Formulaire toujours visible en dessous des filtres */}
      {renderFormContent()}

      {/* Contenu principal selon la vue active */}
      <Box sx={{ px: 1 }}>
        {activeView === 'calendar' && (
          <Stack spacing={1}>
            {filteredDays.map(day => renderDayCard(day, dayPointages[format(day, 'yyyy-MM-dd')]))}
          </Stack>
        )}
        {activeView === 'favorites' && props.renderFavorites()}
        {activeView === 'codes' && renderCodesContent()}
        {activeView === 'email' && renderEmailContent()}
      </Box>

      {/* Menu mobile et SpeedDial */}
      {renderMobileMenu()}
      
      {/* Ajouter le menu contextuel */}
      {renderContextMenu()}

      {/* Ajouter le dialogue de modification */}
      {renderEditDialog()}

      <SpeedDial
        ariaLabel="Menu mobile"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
        onClick={() => setMobileMenuOpen(true)}
      >
        <SpeedDialAction
          icon={<AddIcon />}
          tooltipTitle="Nouveau pointage"
          onClick={() => onQuickAdd(new Date())}
        />
      </SpeedDial>
    </Box>
  );
};

export default PointageMensuelMobile;
